import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { LoaderCircle, Send, X, Plus, CalendarPlus } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'

interface Props {
  isOpen: boolean
  onClose: () => void
  meetingId: string
  baselineEmails: string[]
  alreadySent?: boolean
}

const SendInvitationDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  meetingId,
  baselineEmails,
  alreadySent,
}) => {
  const { t } = useTranslation()
  const [extraInput, setExtraInput] = useState('')
  const [extraEmails, setExtraEmails] = useState<string[]>([])
  const [customMessage, setCustomMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [sendInvitation] = useMutation(Mutation.SEND_MEETING_INVITATION)

  const handleAdd = () => {
    const val = extraInput.trim()
    if (!val) return
    if (!val.includes('@')) {
      toastr.error(t('Common_error'), t('Meeting_invite_invalid_email'))
      return
    }
    if (extraEmails.includes(val)) {
      setExtraInput('')
      return
    }
    setExtraEmails([...extraEmails, val])
    setExtraInput('')
  }

  const handleSend = async () => {
    setSubmitting(true)
    try {
      await sendInvitation({
        variables: {
          id: meetingId,
          extraEmails,
          customMessage: customMessage.trim() || null,
        },
      })
      toastr.success(t('Common_success'), t('Meeting_invite_success'))
      onClose()
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5" />
            {t('Meeting_invite_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {alreadySent && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('Meeting_invite_resend_warning')}
            </div>
          )}

          <div>
            <Label className="block mb-2">{t('Meeting_invite_baseline_label')}</Label>
            <div className="flex flex-wrap gap-1">
              {baselineEmails.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t('Meeting_invite_baseline_empty')}
                </p>
              ) : (
                baselineEmails.map((em) => (
                  <Badge key={em} variant="outline">
                    {em}
                  </Badge>
                ))
              )}
            </div>
          </div>

          <div>
            <Label className="block mb-2">{t('Meeting_invite_extra_label')}</Label>
            <div className="flex gap-2">
              <Input
                placeholder="name@example.com"
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAdd()
                  }
                }}
              />
              <Button variant="outline" size="sm" onClick={handleAdd}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {extraEmails.map((em) => (
                <Badge key={em} variant="outline" className="gap-1">
                  {em}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setExtraEmails(extraEmails.filter((x) => x !== em))}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Label className="block mb-2">{t('Meeting_invite_message_label')}</Label>
            <Textarea
              rows={4}
              placeholder={t('Meeting_invite_message_placeholder')}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {t('Meeting_invite_calendar_hint')}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button onClick={handleSend} disabled={submitting}>
            {submitting ? (
              <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {alreadySent ? t('Meeting_invite_resend') : t('Meeting_invite_send')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SendInvitationDialog
